/**
 * Blinkit-theme category browser — the "left rail + product grid" screen.
 *
 *   /products              rail = All + every vertical        selection → ?cat=<vertical slug>
 *   /c/:slug/products      rail = All + that vertical's groups selection → ?group=<group slug>
 *   /g/:slug               same as the group's vertical, opened on that group (selection kept on the page)
 *
 * Products come from the existing endpoints: ``GET /products`` (cursor-paged, optionally scoped to a vertical)
 * for "All" / a vertical, and ``GET /category-groups/{slug}`` for a group. Both already run the pricing engine.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { LayoutGrid, Loader2, Package, PackageSearch, Search } from "lucide-react";
import { api } from "../../../lib/api";
import { useCategoryGroups, useVerticals } from "../../../lib/catalogData";
import { useCartQuantities } from "../../../lib/useCartQuantities";
import BizCategoryIcon from "../../../components/BizCategoryIcon";
import BkProductTile from "../../../components/blinkit/BkProductTile";
import { BkEmptyState, BkErrorState, BkPageHeader, BkTileSkeletonGrid } from "../../../components/blinkit/BkUI";

const PAGE = 24;

/** Cursor-paged ``GET /products``; resets whenever the vertical changes. */
function usePagedProducts(businessCategoryId, enabled) {
    const [state, setState] = useState({ items: [], cursor: null, hasMore: true, loading: false, error: false, key: null });
    const busy = useRef(false);
    const key = enabled ? (businessCategoryId || "*") : null;

    const load = useCallback(async (cursor, forKey) => {
        if (busy.current) return;
        busy.current = true;
        setState((s) => ({ ...s, loading: true, error: false }));
        try {
            const params = { limit: PAGE };
            if (cursor) params.cursor = cursor;
            if (businessCategoryId) params.business_category_id = businessCategoryId;
            const { data } = await api.get("/products", { params });
            setState((s) => (s.key !== forKey ? s : {
                ...s,
                items: cursor ? [...s.items, ...(data.items || [])] : (data.items || []),
                cursor: data.next_cursor || null,
                hasMore: !!data.next_cursor,
                loading: false,
            }));
        } catch {
            setState((s) => (s.key !== forKey ? s : { ...s, loading: false, error: true, hasMore: false }));
        } finally {
            busy.current = false;
        }
    }, [businessCategoryId]);

    useEffect(() => {
        if (!key) return;
        busy.current = false;
        setState({ items: [], cursor: null, hasMore: true, loading: true, error: false, key });
        load(null, key);
    }, [key, load]);

    return { ...state, loadMore: () => state.cursor && load(state.cursor, key), retry: () => load(null, key) };
}

/** Products of one group (not paged — the endpoint returns the whole group). */
function useGroupProducts(slug) {
    const [state, setState] = useState({ slug: null, data: null, error: false });
    const load = useCallback(() => {
        if (!slug) return;
        setState({ slug, data: null, error: false });
        api.get(`/category-groups/${slug}`)
            .then(({ data }) => setState({ slug, data, error: false }))
            .catch((e) => setState({ slug, data: null, error: e?.response?.status === 404 ? "missing" : true }));
    }, [slug]);
    useEffect(() => { load(); }, [load]);
    return { ...(state.slug === slug ? state : { data: null, error: false }), retry: load };
}

export default function BkCatalogView({ verticalSlug = null, groupSlug = null }) {
    const [params, setParams] = useSearchParams();
    const cartMap = useCartQuantities();
    const verticalsQ = useVerticals();
    const verticals = verticalsQ.data;
    const mode = verticalSlug || groupSlug ? "groups" : "verticals";

    // /g/:slug — the entry group tells us which vertical's rail to show.
    const entry = useGroupProducts(groupSlug);
    const entryVerticalId = entry.data?.group?.business_category_id || null;

    const vertical = useMemo(() => {
        if (!verticals) return null;
        if (verticalSlug) return verticals.find((v) => v.slug === verticalSlug) || null;
        if (entryVerticalId) return verticals.find((v) => v.id === entryVerticalId) || null;
        return null;
    }, [verticals, verticalSlug, entryVerticalId]);

    const groupsReady = mode === "groups" && verticals !== null && (verticalSlug ? true : !!entry.data);
    const groupsQ = useCategoryGroups(vertical?.id, { enabled: groupsReady && (!verticalSlug || !!vertical) });

    // Selection: URL param on /products and /c/:slug/products (shareable, back-button friendly); page state on /g/:slug.
    const paramKey = mode === "verticals" ? "cat" : "group";
    const [localSel, setLocalSel] = useState(groupSlug);
    useEffect(() => { setLocalSel(groupSlug); }, [groupSlug]);
    const sel = groupSlug ? localSel : (params.get(paramKey) || null);
    const select = (key) => {
        if (groupSlug) setLocalSel(key);
        else {
            const next = new URLSearchParams(params);
            if (key) next.set(paramKey, key); else next.delete(paramKey);
            setParams(next, { replace: true });
        }
        window.scrollTo({ top: 0 });
    };

    const rail = useMemo(() => {
        if (mode === "verticals") {
            return (verticals || []).filter((v) => !v.parent_id).map((v) => ({
                key: v.slug, label: v.name, image: v.image_url || v.icon_url, icon: v.icon, id: v.id,
            }));
        }
        return (groupsQ.data || []).map((g) => ({ key: g.slug, label: g.name, image: g.image_url, id: g.id }));
    }, [mode, verticals, groupsQ.data]);

    const selItem = rail.find((r) => r.key === sel) || null;
    const selectedGroup = mode === "groups" && sel ? sel : null;
    const pagedVerticalId = mode === "verticals" ? (selItem?.id || null) : (vertical?.id || null);
    const pagedEnabled = !selectedGroup && verticals !== null && (mode === "verticals" || !!vertical || (!!groupSlug && !!entry.data));
    const paged = usePagedProducts(pagedVerticalId, pagedEnabled);
    const group = useGroupProducts(selectedGroup && selectedGroup !== groupSlug ? selectedGroup : null);
    const groupData = selectedGroup === groupSlug ? entry : group;

    // Infinite scroll for the paged feed.
    const sentinel = useRef(null);
    useEffect(() => {
        const el = sentinel.current;
        if (!el || selectedGroup || !paged.hasMore || !paged.cursor) return undefined;
        const obs = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) paged.loadMore(); }, { rootMargin: "300px" });
        obs.observe(el);
        return () => obs.disconnect();
    }, [selectedGroup, paged.hasMore, paged.cursor]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Not found / failed lookups ──
    const notFound = (verticalSlug && verticals && !vertical) || (groupSlug && entry.error === "missing");
    if (notFound) {
        return (
            <div className="pb-40" data-testid="bk-catalog">
                <BkPageHeader title="Category" back="/categories" />
                <BkEmptyState icon={PackageSearch} testid="bk-catalog-missing" title="This category isn't available"
                              body="It may have been renamed or removed. Browse all categories instead."
                              action={<Link to="/categories" className="inline-flex h-10 items-center px-5 rounded-xl bg-primary text-primary-foreground text-[13px] font-bold">See all categories</Link>} />
            </div>
        );
    }
    if (verticalsQ.error && !verticals?.length) {
        return (
            <div className="pb-40" data-testid="bk-catalog">
                <BkPageHeader title="Categories" />
                <BkErrorState onRetry={() => window.location.reload()} />
            </div>
        );
    }

    const products = selectedGroup ? (groupData.data?.products || null) : (paged.key ? paged.items : null);
    const loading = selectedGroup ? (!groupData.data && !groupData.error) : (paged.loading && paged.items.length === 0) || !pagedEnabled;
    const failed = selectedGroup ? !!groupData.error : paged.error && paged.items.length === 0;
    const title = mode === "verticals"
        ? (selItem?.label || "All products")
        : (vertical?.name || entry.data?.group?.name || "Category");
    const heading = selItem?.label || (mode === "verticals" ? "All products" : `All ${vertical?.name || ""}`.trim());
    const countLabel = products ? `${products.length}${!selectedGroup && paged.hasMore ? "+" : ""} item${products.length === 1 ? "" : "s"}` : null;
    const showRail = rail.length > 0;
    const grid = showRail ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6" : "grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 2xl:grid-cols-7";

    // The rail is sticky inside the row below, so that row must run to the very end of the page: the bottom
    // spacing lives on <main> (not on this wrapper), and ``-mb-24`` takes back the shell's bottom-nav padding
    // (App.js ``pb-24``). Otherwise the last 256px of scrolling drags the rail up under the header. From md up the shell
    // is a card with a 24px bottom margin (+1px border at lg, index.css ``.pwa-shell``), so the rail is that much shorter.
    return (
        <div className="-mb-24 min-w-0" data-testid="bk-catalog" data-mode={mode}>
            <BkPageHeader
                title={title}
                subtitle={countLabel}
                right={<Link to="/search" aria-label="Search" data-testid="bk-catalog-search" className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-muted"><Search size={20} /></Link>}
            />
            <div className="flex items-start">
                {showRail && (
                    <nav aria-label="Categories" data-testid="bk-rail"
                         className="sticky top-14 self-start shrink-0 w-[76px] sm:w-[96px] lg:w-[116px] max-h-[calc(100dvh-3.5rem)] md:max-h-[calc(100dvh-5rem-1px)] overflow-y-auto no-scrollbar bg-card border-r border-border pb-40">
                        <RailItem active={!sel} onClick={() => select(null)} label="All" testid="bk-rail-all" icon={<LayoutGrid size={22} />} />
                        {rail.map((r) => (
                            <RailItem key={r.key} active={sel === r.key} onClick={() => select(r.key)} label={r.label} testid={`bk-rail-${r.key}`}
                                      icon={r.image ? <img src={r.image} alt="" className="w-full h-full object-cover" loading="lazy" />
                                          : mode === "verticals" ? <BizCategoryIcon name={r.icon} size={22} /> : <Package size={22} />} />
                        ))}
                    </nav>
                )}
                <main className="flex-1 min-w-0 px-3 sm:px-4 pt-3 pb-64">
                    <div className="flex items-baseline justify-between gap-2 mb-3">
                        <h2 className="text-[16px] font-extrabold leading-tight truncate" data-testid="bk-catalog-heading">{heading}</h2>
                        {countLabel && <span className="text-[12px] text-muted-foreground shrink-0 tabular-nums">{countLabel}</span>}
                    </div>
                    {loading ? (
                        <BkTileSkeletonGrid count={6} className={grid} testid="bk-catalog-loading" />
                    ) : failed ? (
                        <BkErrorState onRetry={selectedGroup ? groupData.retry : paged.retry} testid="bk-catalog-error" />
                    ) : products && products.length === 0 ? (
                        <BkEmptyState icon={Package} testid="bk-catalog-empty" title="Nothing here yet"
                                      body="Products in this category will show up here as soon as a store adds them."
                                      action={sel && <button type="button" onClick={() => select(null)} className="h-10 px-5 rounded-xl bg-primary text-primary-foreground text-[13px] font-bold">See all products</button>} />
                    ) : (
                        <div className={`grid gap-2.5 sm:gap-3 ${grid}`} data-testid="bk-catalog-grid">
                            {(products || []).map((p) => <BkProductTile key={`${p.restaurant_id}-${p.id}`} product={p} qty={cartMap[p.id] || 0} />)}
                        </div>
                    )}
                    {!selectedGroup && (
                        <div ref={sentinel} className={paged.loading && paged.items.length > 0 ? "flex items-center justify-center gap-2 py-5 text-[12px] text-muted-foreground" : "h-px"} data-testid="bk-catalog-sentinel">
                            {paged.loading && paged.items.length > 0 && <><Loader2 size={14} className="animate-spin" /> Loading more…</>}
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
}

function RailItem({ active, onClick, label, icon, testid }) {
    return (
        <button type="button" onClick={onClick} data-testid={testid} aria-current={active ? "true" : undefined}
                className={`relative w-full flex flex-col items-center gap-1.5 px-1.5 py-3 text-center transition ${active ? "bg-primary/[0.08] text-primary" : "text-foreground hover:bg-muted/60"}`}>
            <span className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl overflow-hidden flex items-center justify-center ${active ? "bg-card ring-2 ring-primary/30" : "bg-primary/[0.06]"} text-primary`}>
                {icon}
            </span>
            <span className={`text-[11px] sm:text-[12px] leading-tight line-clamp-2 break-words max-w-full ${active ? "font-extrabold" : "font-medium"}`}>{label}</span>
            {active && <span className="absolute right-0 top-2 bottom-2 w-[3px] rounded-l-full bg-primary" aria-hidden="true" />}
        </button>
    );
}
